<template>
    <div class="sh-page">
        <div class="sh-header">
            <div class="sh-header-left">
                <div class="sh-icon-wrap"><i class="fas fa-chart-line"></i></div>
                <h2 class="sh-title">Salary Update History</h2>
                <span class="sh-count" v-if="items.length">{{ items.length }}</span>
            </div>
        </div>

        <div v-if="loading" class="sh-loading"><div class="sh-spinner"></div><span>Loading...</span></div>
        <div v-else-if="!items.length" class="sh-empty">
            <div class="sh-empty-icon"><i class="fas fa-chart-line"></i></div>
            <h3>No salary update records found</h3>
        </div>
        <div v-else class="sh-grid">
            <div class="sh-card" v-for="(item, idx) in items" :key="item.id || idx" :style="{ animationDelay: `${idx * 0.03}s` }">
                <div class="sh-card-top">
                    <div class="sh-card-icon" :class="shColor(idx)"><i class="fas fa-chart-line"></i></div>
                    <div class="sh-card-info">
                        <h4 class="sh-card-name">{{ item.employeeName || 'Employee' }}</h4>
                        <span class="sh-card-id" v-if="!employeeId && item.employeeId">
                            <i class="fas fa-id-card"></i> {{ item.employeeId }}
                        </span>
                    </div>
                </div>
                <div class="sh-card-details">
                    <div class="sh-detail">
                        <span class="sh-detail-label">Old CTC</span>
                        <span class="sh-detail-val">{{ formatCurrency(item.oldCtc) }}</span>
                    </div>
                    <div class="sh-detail">
                        <span class="sh-detail-label">New CTC</span>
                        <span class="sh-detail-val sh-val-new">{{ formatCurrency(item.newCtc) }}</span>
                    </div>
                    <div class="sh-detail" v-if="item.incrementPercent != null">
                        <span class="sh-detail-label">Increment</span>
                        <span class="sh-detail-val" :class="item.incrementPercent > 0 ? 'sh-val-up' : 'sh-val-down'">
                            <i :class="item.incrementPercent > 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
                            {{ item.incrementPercent }}%
                        </span>
                    </div>
                </div>
                <div class="sh-card-footer" v-if="item.date">
                    <i class="fas fa-calendar-alt"></i> {{ item.date }}
                </div>
                <div class="sh-card-actions" v-if="canDelete">
                    <button class="sh-action-btn sh-btn-delete" @click="deleteItem(item.id)"><i class="fas fa-trash-alt"></i> Delete</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import { PERMISSION_CODES } from "../../store/permission"

export default {
    props: { employeeId: { type: [String, Number], default: "" } },
    data() {
        return { loading: true, PERMISSION_CODES, items: [], canDelete: true }
    },
    async mounted() { await this.fetchData(); this.loading = false },
    methods: {
        shColor(i) { return ["sh-clr-blue","sh-clr-purple","sh-clr-teal","sh-clr-amber","sh-clr-rose"][i % 5] },
        formatCurrency(v) { if (v == null) return '—'; return Number(v).toLocaleString('en-IN') },
        async fetchData() {
            try {
                const url = this.employeeId ? `/salary-update-histories?employee_id=${this.employeeId}` : '/salary-update-histories'
                const r = await http.get(url)
                this.items = r.data.result || r.data || []
            } catch(e) { console.log(e) }
        },
        async deleteItem(id) {
            if (!confirm("Delete this record?")) return
            try { await http.delete(`/salary-update-histories/${id}`); this.$toast.success("Deleted"); this.items = this.items.filter(c => c.id !== id) }
            catch(e) { this.$toast.error("Error deleting") }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.sh-page { animation: shFade .3s ease; }
@keyframes shFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.sh-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.sh-header-left { display:flex; align-items:center; gap:10px; }
.sh-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.sh-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.sh-count { min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull; font-size:11px; font-weight:700; background:$primaryColor; color:white; display:inline-flex; align-items:center; justify-content:center; }
.sh-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.sh-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:shSpin .7s linear infinite; }
@keyframes shSpin { to { transform:rotate(360deg); } }
.sh-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.sh-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }
.sh-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.sh-card { background:white; border:1px solid $gray100; border-radius:10px; padding:14px; box-shadow:$shadowSm; display:flex; flex-direction:column; gap:10px; animation:shSlide .25s ease both; transition:all $transitionFast; &:hover { border-color:$gray200; box-shadow:$shadowMd; transform:translateY(-1px); } }
@keyframes shSlide { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.sh-card-top { display:flex; align-items:flex-start; gap:10px; }
.sh-card-icon { width:36px; height:36px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.sh-clr-blue { background:#eff6ff; color:#2563eb; }
.sh-clr-purple { background:#f5f3ff; color:#7c3aed; }
.sh-clr-teal { background:#f0fdfa; color:#0d9488; }
.sh-clr-amber { background:#fffbeb; color:#d97706; }
.sh-clr-rose { background:#fff1f2; color:#e11d48; }
.sh-card-info { flex:1; min-width:0; }
.sh-card-name { font-size:13.5px; font-weight:700; color:$gray800; margin:0 0 3px; line-height:1.3; }
.sh-card-id { font-size:11px; color:$gray400; font-weight:500; display:inline-flex; align-items:center; gap:4px; i { font-size:10px; } }
.sh-card-details { display:flex; flex-wrap:wrap; gap:6px; }
.sh-detail { flex:1; min-width:70px; background:$gray50; border:1px solid $gray100; border-radius:6px; padding:6px 10px; display:flex; flex-direction:column; gap:2px; }
.sh-detail-label { font-size:10px; color:$gray400; font-weight:500; text-transform:uppercase; letter-spacing:0.3px; }
.sh-detail-val { font-size:12px; color:$gray700; font-weight:600; }
.sh-val-new { color:$primaryColor; }
.sh-val-up { color:#16a34a; i { font-size:9px; } }
.sh-val-down { color:#dc2626; i { font-size:9px; } }
.sh-card-footer { font-size:11px; color:$gray400; font-weight:500; display:flex; align-items:center; gap:5px; i { font-size:10px; } }
.sh-card-actions { display:flex; gap:6px; margin-top:auto; padding-top:6px; border-top:1px solid $gray100; }
.sh-action-btn { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; background:white; i { font-size:10px; } }
.sh-btn-delete { color:$danger; &:hover { background:#fef2f2; border-color:rgba(220,38,38,0.15); } }
@media(max-width:992px) { .sh-grid { grid-template-columns:repeat(2,1fr); } }
@media(max-width:480px) { .sh-grid { grid-template-columns:1fr; } }
</style>
